import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// UI STORE STATE
// ============================================================================

export interface UIState {
  // Theme and appearance
  theme: 'monochrome' | 'monochrome-dark';
  sidebarOpen: boolean;
  
  // Modal states
  modals: {
    [key: string]: boolean;
  };
  
  // Navigation
  currentPage: string;
  breadcrumbs: Array<{
    label: string;
    path: string;
  }>;
  
  // Notifications
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration?: number;
    timestamp: string;
  }>;
  
  // Loading states
  loadingStates: {
    [key: string]: boolean;
  };
  
  // User preferences
  preferences: {
    autoSave: boolean;
    notifications: boolean;
    compactMode: boolean;
    language: string;
  };
}

// ============================================================================
// UI STORE ACTIONS
// ============================================================================

export interface UIActions {
  // Theme management
  setTheme: (theme: 'monochrome' | 'monochrome-dark') => void;
  toggleTheme: () => void;
  
  // Sidebar management
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  
  // Modal management
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  closeAllModals: () => void;
  
  // Navigation
  setCurrentPage: (page: string) => void;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path: string }>) => void;
  
  // Notifications
  addNotification: (notification: Omit<UIState['notifications'][0], 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Loading states
  setLoading: (key: string, loading: boolean) => void;
  clearLoading: (key: string) => void;
  clearAllLoading: () => void;
  
  // Preferences
  updatePreference: <K extends keyof UIState['preferences']>(
    key: K,
    value: UIState['preferences'][K]
  ) => void;
  resetPreferences: () => void;
  
  // Utility actions
  reset: () => void;
}

// ============================================================================
// UI STORE TYPE
// ============================================================================

export type UIStore = UIState & UIActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: UIState = {
  theme: 'monochrome',
  sidebarOpen: true,
  modals: {},
  currentPage: '/',
  breadcrumbs: [],
  notifications: [],
  loadingStates: {},
  preferences: {
    autoSave: true,
    notifications: true,
    compactMode: false,
    language: 'en',
  },
};

// ============================================================================
// UI STORE IMPLEMENTATION
// ============================================================================

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ============================================================================
      // THEME MANAGEMENT
      // ============================================================================

      setTheme: (theme: 'monochrome' | 'monochrome-dark') => {
        set({ theme });
      },

      toggleTheme: () => {
        const { theme } = get();
        set({ theme: theme === 'monochrome' ? 'monochrome-dark' : 'monochrome' });
      },

      // ============================================================================
      // SIDEBAR MANAGEMENT
      // ============================================================================

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebar: () => {
        const { sidebarOpen } = get();
        set({ sidebarOpen: !sidebarOpen });
      },

      // ============================================================================
      // MODAL MANAGEMENT
      // ============================================================================

      openModal: (modalId: string) => {
        set((state) => ({
          modals: { ...state.modals, [modalId]: true },
        }));
      },

      closeModal: (modalId: string) => {
        set((state) => ({
          modals: { ...state.modals, [modalId]: false },
        }));
      },

      closeAllModals: () => {
        set({ modals: {} });
      },

      // ============================================================================
      // NAVIGATION
      // ============================================================================

      setCurrentPage: (page: string) => {
        set({ currentPage: page });
      },

      setBreadcrumbs: (breadcrumbs: Array<{ label: string; path: string }>) => {
        set({ breadcrumbs });
      },

      // ============================================================================
      // NOTIFICATIONS
      // ============================================================================

      addNotification: (notification) => {
        const newNotification = {
          ...notification,
          id: Math.random().toString(36).substr(2, 9),
          timestamp: new Date().toISOString(),
        };

        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Auto-remove notification after duration
        if (notification.duration) {
          setTimeout(() => {
            get().removeNotification(newNotification.id);
          }, notification.duration);
        }
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      // ============================================================================
      // LOADING STATES
      // ============================================================================

      setLoading: (key: string, loading: boolean) => {
        set((state) => ({
          loadingStates: { ...state.loadingStates, [key]: loading },
        }));
      },

      clearLoading: (key: string) => {
        set((state) => {
          const newLoadingStates = { ...state.loadingStates };
          delete newLoadingStates[key];
          return { loadingStates: newLoadingStates };
        });
      },

      clearAllLoading: () => {
        set({ loadingStates: {} });
      },

      // ============================================================================
      // PREFERENCES
      // ============================================================================

      updatePreference: (key, value) => {
        set((state) => ({
          preferences: { ...state.preferences, [key]: value },
        }));
      },

      resetPreferences: () => {
        set({ preferences: initialState.preferences });
      },

      // ============================================================================
      // UTILITY ACTIONS
      // ============================================================================

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'mirray-ui-store',
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        preferences: state.preferences,
      }),
    }
  )
); 